import CloseIcon from "@mui/icons-material/Close";
import ExtensionIcon from "@mui/icons-material/Extension";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAppCopyrightLine } from "../../config/appCopyright";
import { usePagesTranslation } from "../locales";
import SocialShareDropdown, {
    buildStorySharePageUrl,
} from "../puzzles/common/components/SocialShareDropdown";
import { ACCENT } from "../../dark-theme/palette";
import { playPillButtonSx } from "../../dark-theme/surfaces";

/**
 * Full story text + optional image + share controls (same content as StoryPlayStoryAccordion details).
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   summaryTitle: string,
 *   imageUrl?: string,
 *   children?: React.ReactNode,
 *   storyShareId?: string,
 *   storyShareTitle?: string,
 *   storySharePuzzleType?: string,
 *   storyId?: string,
 *   showSlidingPuzzle?: boolean,
 *   showJigsawPuzzle?: boolean,
 * }} props
 */
export default function StoryPlayStoryDialog({
    open,
    onClose,
    summaryTitle,
    imageUrl,
    children,
    storyShareId,
    storyShareTitle,
    storySharePuzzleType,
    storyId,
    showSlidingPuzzle = false,
    showJigsawPuzzle = false,
}) {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const [storyImageDialogOpen, setStoryImageDialogOpen] = useState(false);
    const genericHeading = String(t("puzzle.playCross.story-card-heading")).trim();
    const raw = String(summaryTitle ?? "").trim();
    const prefixLabel = String(t("puzzle.playCross.story-title-prefix", "Story:"));
    const spacer = prefixLabel.endsWith(" ") ? "" : " ";
    const headingText =
        !raw || raw === genericHeading ? genericHeading : `${prefixLabel}${spacer}${raw}`;

    const storyShareUrl = useMemo(() => {
        if (!storyShareId) return "";
        return buildStorySharePageUrl(storyShareId) || "";
    }, [storyShareId]);

    const shareLabel = useMemo(() => {
        const label = String(storyShareTitle ?? "").trim();
        return label || genericHeading;
    }, [storyShareTitle, genericHeading]);

    const showStoryShare =
        Boolean(storyShareUrl && storyShareId && storySharePuzzleType);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle
                sx={{
                    position: "relative",
                    pr: 6,
                    pb: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        pr: 1,
                        minWidth: 0,
                    }}
                >
                    <Typography
                        component="span"
                        variant="subtitle1"
                        sx={{
                            flex: "1 1 auto",
                            minWidth: 0,
                            fontWeight: 600,
                            color: ACCENT.light,
                            fontSize: "0.95rem",
                            lineHeight: 1.35,
                            wordBreak: "break-word",
                        }}
                    >
                        {headingText}
                    </Typography>
                    {showStoryShare ? (
                        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                            <SocialShareDropdown
                                compact
                                puzzleTitle={shareLabel}
                                puzzleId={storyShareId}
                                puzzleType={storySharePuzzleType}
                                shareUrl={storyShareUrl}
                                shareMessage={t("puzzle.playCross.story-share-message", {
                                    title: shareLabel,
                                })}
                                tooltipTitle={t("puzzle.playCross.story-share-tooltip")}
                            />
                        </Box>
                    ) : null}
                </Box>
                <IconButton
                    type="button"
                    onClick={onClose}
                    aria-label={t("puzzle.wordMeaning.close-aria")}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: "text.secondary",
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2, px: { xs: 2, sm: 3 }, pb: 2 }}>
                {imageUrl ? (
                    <>
                        <Box
                            sx={{
                                position: "relative",
                                mb: 1.5,
                                borderRadius: 1,
                                overflow: "hidden",
                                bgcolor: "action.hover",
                            }}
                        >
                            <Box
                                component="img"
                                src={imageUrl}
                                alt=""
                                sx={{
                                    width: "100%",
                                    maxHeight: 200,
                                    objectFit: "contain",
                                    display: "block",
                                    verticalAlign: "top",
                                }}
                            />
                            <Tooltip
                                title={t(
                                    "puzzle.playCross.story-image-view-large",
                                    "View larger image"
                                )}
                                arrow
                            >
                                <IconButton
                                    type="button"
                                    size="small"
                                    onClick={() => setStoryImageDialogOpen(true)}
                                    aria-label={t(
                                        "puzzle.playCross.story-image-view-large",
                                        "View larger image"
                                    )}
                                    sx={{
                                        ...playPillButtonSx,
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                    }}
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {/* Puzzle navigation icons */}
                            {storyId && (showSlidingPuzzle || showJigsawPuzzle) && (
                                <Box sx={{ position: "absolute", bottom: 8, right: 8, display: "flex", gap: 0.5 }}>
                                    {showSlidingPuzzle && (
                                        <Tooltip title="Sliding Puzzle Quest" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => { onClose(); navigate(`/puzzle/play-sliding/${storyId}`); }}
                                                sx={playPillButtonSx}
                                            >
                                                <ViewCarouselIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {showJigsawPuzzle && (
                                        <Tooltip title="Jigsaw Muse" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => { onClose(); navigate(`/puzzle/play-jigsaw/${storyId}`); }}
                                                sx={playPillButtonSx}
                                            >
                                                <ExtensionIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            )}
                        </Box>
                        <Dialog
                            open={storyImageDialogOpen}
                            onClose={() => setStoryImageDialogOpen(false)}
                            maxWidth="lg"
                            fullWidth
                            PaperProps={{
                                sx: { bgcolor: "background.default" },
                            }}
                        >
                            <DialogContent sx={{ p: 1.5, position: "relative" }}>
                                <IconButton
                                    type="button"
                                    onClick={() => setStoryImageDialogOpen(false)}
                                    aria-label={t("puzzle.wordMeaning.close-aria")}
                                    sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        zIndex: 1,
                                        bgcolor: "background.paper",
                                        boxShadow: 1,
                                        "&:hover": { bgcolor: "background.paper" },
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt=""
                                    sx={{
                                        width: "100%",
                                        maxHeight: "min(85vh, 900px)",
                                        objectFit: "contain",
                                        display: "block",
                                        mx: "auto",
                                    }}
                                />
                            </DialogContent>
                        </Dialog>
                    </>
                ) : null}
                {children != null ? (
                    <Typography
                        component="div"
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.65 }}
                    >
                        {children}
                    </Typography>
                ) : null}
                {imageUrl && getAppCopyrightLine() ? (
                    <Box sx={{ mt: 1.5 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.35 }}
                        >
                            {getAppCopyrightLine()}
                        </Typography>
                    </Box>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
